import { Link } from "react-router-dom";
import { ShieldCheck, TrendingUp, Users, Wallet, BadgeCheck, Sparkles, ArrowRight } from "lucide-react";

const PROOF = [
  { icon: BadgeCheck, label: "Pre-vetted by frantiger" },
  { icon: ShieldCheck, label: "Backed by CCD / NCD instruments" },
  { icon: Wallet, label: "Start from ₹5,000" },
  { icon: Users, label: "Max 200 investors per pool" },
  { icon: TrendingUp, label: "Secondary liquidity — Offer to Buy / Sell" },
];

export default function Hero() {
  return (
    <section id="hero" data-testid="hero-section" className="hero-glow relative overflow-hidden pb-20 pt-32 sm:pt-36">
      <div className="mx-auto grid max-w-6xl items-center gap-14 px-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <div className="reveal inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-orange-600" data-testid="hero-badge">
            <Sparkles className="h-3.5 w-3.5" />
            India's Fractional Franchise Investment Platform
          </div>
          <h1 className="reveal d1 font-heading mt-6 text-4xl font-black leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Own a fraction of real, cash-flowing franchises.{" "}
            <span className="text-orange-500">Start from just ₹5,000.</span>
          </h1>
          <p className="reveal d2 mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            fraction is a peer-to-peer franchise investment platform. You subscribe to fractions of
            a pre-vetted outlet through private debt instruments — CCDs / NCDs — that sit alongside
            the operator's own capital in a disciplined 60:40 structure. Professional operators run
            the outlet; you earn monthly coupons and exit early on the secondary board.
          </p>
          <div className="reveal d3 mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#lead-form"
              data-testid="hero-join-waitlist-btn"
              className="clay-btn rounded-full bg-orange-500 px-8 py-4 text-base font-bold text-white"
            >
              Join the Investor Waitlist
            </a>
            <a
              href="#how-it-works"
              data-testid="hero-how-it-works-btn"
              className="clay-btn-dark rounded-full bg-white px-8 py-4 text-base font-bold text-slate-800"
            >
              How Fractional Funding Works
            </a>
          </div>
          <div className="reveal d4 mt-10 flex flex-wrap gap-3" data-testid="hero-proof-strip">
            {PROOF.map(({ icon: Icon, label }) => (
              <span key={label} className="neu-badge inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold text-slate-700">
                <Icon className="h-3.5 w-3.5 text-orange-500" />
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="reveal d2 relative" data-testid="hero-funding-card">
          <div className="glass-surface relative z-10 rounded-[2rem] p-7 shadow-2xl shadow-orange-500/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Live preview</p>
                <h3 className="font-heading mt-2 text-xl font-extrabold text-slate-900">Chai &amp; Coffee Hub</h3>
                <p className="text-sm text-slate-500">Indiranagar, Bengaluru · QSR Cafe</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-orange-500">Scrip: MCDHSR · CCD-backed</p>
              </div>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">Funding Open</span>
            </div>

            <div className="mt-6">
              <div className="flex items-end justify-between text-sm">
                <span className="font-bold text-slate-700">₹32.4L raised</span>
                <span className="font-black text-orange-600">72% funded</span>
              </div>
              <div className="mt-2 h-3.5 overflow-hidden rounded-full bg-slate-200/80" data-testid="hero-progress-bar">
                <div className="progress-glow h-full w-[72%] rounded-full bg-gradient-to-r from-orange-400 to-orange-600" />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { label: "Min. fraction", value: "₹5,000" },
                { label: "Pool target (40%)", value: "₹45,00,000" },
                { label: "Investors", value: "144 / 200" },
              ].map((s) => (
                <div key={s.label} className="clay-card rounded-2xl p-3 text-center">
                  <p className="font-heading text-sm font-extrabold text-slate-900 sm:text-base">{s.value}</p>
                  <p className="mt-1 text-[11px] font-semibold text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3 rounded-2xl bg-slate-900 px-4 py-3 text-white">
              <Users className="h-5 w-5 shrink-0 text-orange-400" />
              <p className="text-xs leading-relaxed text-slate-300">
                Operator holds 60% of the capital stack; investors subscribe to the 40% pool and
                receive monthly coupons plus transparent sales reporting.
              </p>
            </div>
            <Link
              to="/scrip/MCDHSR"
              data-testid="hero-view-scrip-link"
              className="mt-5 flex items-center justify-center gap-1.5 rounded-full border border-orange-200 bg-orange-50/70 px-4 py-2.5 text-xs font-bold text-orange-600 transition-colors hover:bg-orange-100"
            >
              View full scrip details &amp; live order book <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <p className="mt-3 text-center text-[10px] text-slate-400">Illustrative preview. Not an offer to invest.</p>
          </div>

          <div className="float-soft absolute -left-4 -top-6 z-20 hidden rounded-2xl bg-white px-4 py-3 shadow-xl shadow-slate-900/10 sm:block">
            <p className="text-[11px] font-bold text-slate-500">Est. monthly coupon</p>
            <p className="font-heading text-lg font-black text-emerald-600">₹93 <span className="text-xs font-semibold text-slate-400">/ ₹5K</span></p>
          </div>
          <div className="float-soft absolute -bottom-6 -right-3 z-20 hidden rounded-2xl bg-orange-500 px-4 py-3 text-white shadow-xl shadow-orange-500/40 sm:block" style={{ animationDelay: "1.2s" }}>
            <p className="text-[11px] font-bold text-orange-100">Est. gross yield (IRR)</p>
            <p className="font-heading text-lg font-black">18–22%</p>
          </div>
        </div>
      </div>
    </section>
  );
}
